/**
 * SkillDelivery Platform
 *
 * Copyright 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 */

import React, { useState, useEffect, useCallback } from 'react';
import GoalCard from './GoalCard';
import './GoalsSection.css';

const RAW_URL = process.env.REACT_APP_API_URL || 'https://skilldelivery-platform.onrender.com';
const API_URL = RAW_URL.endsWith('/api') ? RAW_URL.slice(0, -4) : RAW_URL;



const GoalsSection = () => {
  const [goals, setGoals] = useState([]);
  const [insights, setInsights] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddGoal, setShowAddGoal] = useState(false);
  const [newGoal, setNewGoal] = useState({
    goal_type: '',
    goal_text: '',
    priority: 5,
    goal_description: '',
    target_date: ''
  });

  const fetchGoals = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_URL}/api/goals`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch goals');
      }

      const data = await response.json();

      if (data.success) {
        setGoals(data.goals || []);
        setInsights(data.insights || []);
        // Deduplicate templates by goal_type to prevent duplicate quick-select chips
        const rawTemplates = data.templates || [];
        const seen = new Set();
        const uniqueTemplates = rawTemplates.filter(t => {
          if (seen.has(t.goal_type)) return false;
          seen.add(t.goal_type);
          return true;
        });
        setTemplates(uniqueTemplates);
      }
    } catch (err) {
      console.error('Error fetching goals:', err);
      setError('Failed to load goals');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGoals();
  }, [fetchGoals]);

  const handlePriorityChange = async (goalId, newPriority) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/goals/${goalId}/priority`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ priority: newPriority })
      });

      if (response.ok) {
        setGoals(prev => prev.map(g =>
          g.id === goalId ? { ...g, priority: newPriority } : g
        ).sort((a, b) => b.priority - a.priority));
      }
    } catch (err) {
      console.error('Error updating priority:', err);
    }
  };

  const handleStatusChange = async (goalId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/goals/${goalId}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        const data = await response.json();
        setGoals(prev => prev.map(g =>
          g.id === goalId ? { ...g, status: newStatus, achieved_at: data.goal?.achieved_at } : g
        ));
      }
    } catch (err) {
      console.error('Error updating status:', err);
    }
  };

  const handleProgressChange = async (goalId, newProgress) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/goals/${goalId}/progress`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ progress: newProgress })
      });

      setGoals(prev => prev.map(g =>
        g.id === goalId ? { ...g, progress: newProgress } : g
      ));
    } catch (err) {
      console.error('Error updating progress:', err);
    }
  };

  const handleDeleteGoal = async (goalId) => {
    if (!window.confirm('Are you sure you want to delete this goal?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/goals/${goalId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setGoals(prev => prev.filter(g => g.id !== goalId));
      }
    } catch (err) {
      console.error('Error deleting goal:', err);
    }
  };

  const handleAddGoal = async (e) => {
    e.preventDefault();
    if (!newGoal.goal_text.trim()) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/goals`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...newGoal,
          goal_type: newGoal.goal_type || 'custom'
        })
      });

      const data = await response.json();
      if (data.success) {
        setGoals(prev => [data.goal, ...prev].sort((a, b) => b.priority - a.priority));
        setNewGoal({ goal_type: '', goal_text: '', priority: 5, goal_description: '', target_date: '' });
        setShowAddGoal(false);
      }
    } catch (err) {
      console.error('Error adding goal:', err);
    }
  };

  const selectTemplate = (template) => {
    setNewGoal(prev => ({
      ...prev,
      goal_type: template.goal_type,
      goal_text: template.goal_text
    }));
  };

  const dismissInsight = async (insightId) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/goals/insights/${insightId}/dismiss`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setInsights(prev => prev.filter(i => i.id !== insightId));
    } catch (err) {
      console.error('Error dismissing insight:', err);
    }
  };

  // Separate general insights from goal-specific ones
  const generalInsights = insights.filter(i => !i.goal_id);
  const activeGoals = goals.filter(g => g.status === 'active');
  const otherGoals = goals.filter(g => g.status !== 'active');

  if (loading) {
    return (
      <div className="goals-section loading">
        <div className="loading-spinner"></div>
        <p>Loading goals...</p>
      </div>
    );
  }

  return (
    <div className="goals-section">
      {/* Header */}
      <div className="goals-section-header">
        <div className="goals-title-area">
          <span className="goals-icon">🎯</span>
          <div>
            <h2>My Goals & Priorities</h2>
            <p>Drag the slider to adjust priority (1-10)</p>
          </div>
        </div>
        <button
          className="add-goal-button"
          onClick={() => setShowAddGoal(!showAddGoal)}
        >
          {showAddGoal ? '✕ Cancel' : '+ Add Goal'}
        </button>
      </div>

      {/* Platform Insights Panel */}
      {generalInsights.length > 0 && (
        <div className="platform-insights-panel">
          <h3>📢 Platform Insights</h3>
          {generalInsights.map(insight => (
            <div
              key={insight.id}
              className={`platform-insight severity-${insight.severity}`}
            >
              <div className="platform-insight-content">
                <strong>{insight.title}</strong>
                <p>{insight.message}</p>
              </div>
              <div className="platform-insight-actions">
                {insight.action_url && (
                  <a href={insight.action_url} className="insight-action-btn">
                    {insight.action_text || 'View'}
                  </a>
                )}
                <button
                  className="insight-dismiss-btn"
                  onClick={() => dismissInsight(insight.id)}
                  aria-label="Dismiss"
                >
                  ✕
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Goal Form */}
      {showAddGoal && (
        <div className="add-goal-form">
          <h3>Add New Goal</h3>

          {/* Template Quick Select */}
          {templates.length > 0 && (
            <div className="goal-templates-section">
              <p className="templates-label">Quick select a goal:</p>
              <div className="template-chips">
                {templates.map(t => (
                  <button
                    key={t.id}
                    type="button"
                    className={`template-chip ${newGoal.goal_type === t.goal_type ? 'selected' : ''}`}
                    onClick={() => selectTemplate(t)}
                  >
                    {t.goal_text}
                  </button>
                ))}
              </div>
            </div>
          )}

          <form onSubmit={handleAddGoal}>
            <div className="form-field">
              <label htmlFor="goal_text">Goal Description *</label>
              <input
                id="goal_text"
                type="text"
                value={newGoal.goal_text}
                onChange={(e) => setNewGoal(prev => ({ ...prev, goal_text: e.target.value }))}
                placeholder="What do you want to achieve?"
                required
              />
            </div>

            <div className="form-field">
              <label htmlFor="goal_description">Details (optional)</label>
              <textarea
                id="goal_description"
                value={newGoal.goal_description}
                onChange={(e) => setNewGoal(prev => ({ ...prev, goal_description: e.target.value }))}
                placeholder="Add more details about your goal..."
                rows={2}
              />
            </div>

            <div className="form-row">
              <div className="form-field">
                <label htmlFor="priority">Initial Priority: {newGoal.priority}/10</label>
                <input
                  id="priority"
                  type="range"
                  min="1"
                  max="10"
                  value={newGoal.priority}
                  onChange={(e) => setNewGoal(prev => ({ ...prev, priority: parseInt(e.target.value) }))}
                />
              </div>

              <div className="form-field">
                <label htmlFor="target_date">Target Date (optional)</label>
                <input
                  id="target_date"
                  type="date"
                  value={newGoal.target_date}
                  onChange={(e) => setNewGoal(prev => ({ ...prev, target_date: e.target.value }))}
                />
              </div>
            </div>

            <button type="submit" className="submit-goal-btn">
              Add Goal
            </button>
          </form>
        </div>
      )}

      {/* Goals List */}
      {goals.length === 0 ? (
        <div className="no-goals-state">
          <div className="no-goals-icon">🎯</div>
          <h3>No goals yet</h3>
          <p>Set your first goal to start tracking your progress!</p>
          <button onClick={() => setShowAddGoal(true)} className="add-first-goal-btn">
            + Add Your First Goal
          </button>
        </div>
      ) : (
        <>
          {/* Active Goals */}
          {activeGoals.length > 0 && (
            <div className="goals-list active-goals">
              {activeGoals.map(goal => (
                <GoalCard
                  key={goal.id}
                  goal={goal}
                  onPriorityChange={handlePriorityChange}
                  onStatusChange={handleStatusChange}
                  onProgressChange={handleProgressChange}
                  onDelete={handleDeleteGoal}
                  insights={insights}
                />
              ))}
            </div>
          )}

          {/* Other Goals (paused, achieved, abandoned) */}
          {otherGoals.length > 0 && (
            <div className="other-goals-section">
              <h4 className="other-goals-header">
                Other Goals ({otherGoals.length})
              </h4>
              <div className="goals-list other-goals">
                {otherGoals.map(goal => (
                  <GoalCard
                    key={goal.id}
                    goal={goal}
                    onPriorityChange={handlePriorityChange}
                    onStatusChange={handleStatusChange}
                    onProgressChange={handleProgressChange}
                    onDelete={handleDeleteGoal}
                    insights={insights}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default GoalsSection;
